import React, { useState, useMemo } from 'react';
import { ChevronDown, ChevronUp, Search, X, Layers } from 'lucide-react';
import apiClient from '../config/axios';
import { formatCR } from '../utils/formatCR';

const ABILITIES = [
  ['str', 'STR'], ['dex', 'DEX'], ['con', 'CON'],
  ['int', 'INT'], ['wis', 'WIS'], ['cha', 'CHA'],
];

/**
 * The whole set, in one request (P4 R12).
 *
 * `routes/templates.js` returned every row with no pagination at all; the
 * generic list route paginates like every other list (allowlist A12), so the
 * panel asks for the platform maximum. 3.5 has ~150 templates and pf-1e has
 * 14 (issue 822), which is the same "the set is small" premise the deleted
 * route stated in its own header. Pathfinder's rules text is multi-line (one
 * labelled rule per line), hence `whitespace-pre-line` on the rules block.
 */
const TEMPLATE_PAGE_SIZE = 200;

/**
 * The six ability deltas, read off the FLAT keys the model stores.
 *
 * The v1 row carried one `ability_modifiers` jsonb blob keyed `Str`/`Dex`/…;
 * the template family declares six ordinary number attributes under an
 * `ability_modifiers` GROUP, which is the same normalization the monster
 * stat block got at R11 — one key per concept, no blob to parse. The panel adds
 * them exactly as it added the blob's entries.
 */
const templateMods = (template) => {
  const out = {};
  for (const [key, label] of ABILITIES) {
    const value = Number(template[key]);
    if (Number.isFinite(value) && value !== 0) out[label] = value;
  }
  return out;
};

const fmtDelta = (n) => (n >= 0 ? `+${n}` : `${n}`);

/**
 * Creature Templates panel (#19) for the monster detail page. A collapsible,
 * searchable list of the active system's creature templates; checking one or more shows how
 * they change the monster — combined ability-score deltas (applied to the
 * monster's base scores), CR adjustment, natural-armor bonus, and each
 * template's size/type change and special attacks/qualities prose.
 *
 * Scope note: this shows the effect of the selected templates; it does NOT yet
 * hard-filter to only the templates a given monster can *legally* take (the
 * source data's restrictions are free prose that doesn't parse reliably), and
 * it does not emit a single fully-recomputed stat block. See DRAFT_NOTES_19.md.
 */
function MonsterTemplates({ monster }) {
  const [expanded, setExpanded] = useState(false);
  const [templates, setTemplates] = useState(null); // null = not fetched yet
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [selectedSlugs, setSelectedSlugs] = useState([]);

  const toggleExpanded = () => {
    setExpanded((v) => !v);
    if (templates === null && !loading) {
      setLoading(true);
      apiClient
        .get(`/api/templates?limit=${TEMPLATE_PAGE_SIZE}`)
        .then((res) => setTemplates(res.data?.templates || []))
        .catch(() => setError('Could not load templates.'))
        .finally(() => setLoading(false));
    }
  };

  const toggleSelect = (slug) => {
    setSelectedSlugs((prev) =>
      prev.includes(slug) ? prev.filter((s) => s !== slug) : [...prev, slug]
    );
  };

  const filtered = useMemo(() => {
    if (!templates) return [];
    const q = query.trim().toLowerCase();
    if (!q) return templates;
    return templates.filter((t) => t.name.toLowerCase().includes(q));
  }, [templates, query]);

  const selected = useMemo(
    () => (templates || []).filter((t) => selectedSlugs.includes(t.slug)),
    [templates, selectedSlugs]
  );

  // Combined ability deltas across all selected templates (deltas are additive).
  const combinedMods = useMemo(() => {
    const acc = {};
    for (const t of selected) {
      for (const [k, v] of Object.entries(templateMods(t))) acc[k] = (acc[k] || 0) + v;
    }
    return acc;
  }, [selected]);

  const totalCrAdj = useMemo(
    () => selected.reduce((sum, t) => sum + (t.cr_adjustment || 0), 0),
    [selected]
  );
  const totalNaturalArmor = useMemo(
    () => selected.reduce((sum, t) => sum + (t.natural_armor_bonus || 0), 0),
    [selected]
  );

  const baseCr = Number(monster.challenge_rating);
  const newCr = Number.isNaN(baseCr) ? null : baseCr + totalCrAdj;

  // Rows for the modified-ability-scores table: any ability the monster has a
  // score for, or that a selected template modifies. Ability scores arrive from
  // pg as strings, so coerce to Number — otherwise `base + delta` concatenates.
  const abilityRows = ABILITIES
    .map(([col, label]) => {
      const raw = monster[col];
      const n = raw == null || raw === '' ? null : Number(raw);
      const base = Number.isNaN(n) ? null : n;
      const delta = combinedMods[label] || 0;
      return { col, label, base, delta };
    })
    .filter((r) => r.base != null || r.delta !== 0);

  return (
    // An interactive calculator, not content — it stays off paper (#194).
    <div className="card print:hidden">
      <button
        type="button"
        onClick={toggleExpanded}
        aria-expanded={expanded}
        className="w-full flex items-center justify-between gap-3 text-left"
      >
        <span className="flex items-center gap-2 section-header" style={{ marginBottom: 0 }}>
          <Layers className="w-5 h-5 text-dnd-purple" />
          Creature Templates
        </span>
        <span className="flex items-center gap-2 text-muted text-sm">
          {selectedSlugs.length > 0 && (
            <span className="badge badge-purple">{selectedSlugs.length} selected</span>
          )}
          {expanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
        </span>
      </button>

      {expanded && (
        <div className="mt-4 space-y-4">
          <p className="text-muted text-sm">
            Select one or more templates to preview how they modify{' '}
            <span className="font-semibold">{monster.name}</span>. Deltas stack; the
            listed rules text shows each template's other changes.
          </p>

          {/* Search */}
          <div className="relative">
            <Search
              className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4"
              style={{ color: 'var(--text-muted)' }}
            />
            <input
              type="text"
              className="input pl-9"
              placeholder="Search templates…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              aria-label="Search templates"
            />
          </div>

          {loading && <p className="text-muted text-sm">Loading templates…</p>}
          {error && <p className="text-sm text-danger">{error}</p>}

          {!loading && !error && templates && (
            <>
              {/* Scrollable checkbox list */}
              <div
                className="rounded-lg overflow-y-auto"
                style={{ maxHeight: '18rem', border: '1px solid var(--border)' }}
                role="group"
                aria-label="Applicable templates"
              >
                {filtered.length === 0 ? (
                  <p className="text-muted text-sm p-3">No templates match “{query}”.</p>
                ) : (
                  <ul>
                    {filtered.map((t) => {
                      const checked = selectedSlugs.includes(t.slug);
                      return (
                        <li key={t.slug} style={{ borderBottom: '1px solid var(--border)' }}>
                          <label className="flex items-start gap-3 p-2.5 cursor-pointer hover:bg-[var(--bg-raised)]">
                            <input
                              type="checkbox"
                              className="mt-1 flex-shrink-0"
                              checked={checked}
                              onChange={() => toggleSelect(t.slug)}
                            />
                            <span className="min-w-0">
                              <span className="font-medium">{t.name}</span>
                              <span className="flex flex-wrap gap-1.5 mt-1">
                                {t.cr_adjustment != null && (
                                  <span className="badge badge-red">CR {fmtDelta(t.cr_adjustment)}</span>
                                )}
                                {Object.entries(templateMods(t)).map(([k, v]) => (
                                  <span key={k} className="badge badge-blue">{k} {fmtDelta(v)}</span>
                                ))}
                                {t.natural_armor_bonus != null && (
                                  <span className="badge badge-green">NA {fmtDelta(t.natural_armor_bonus)}</span>
                                )}
                              </span>
                            </span>
                          </label>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>

              {/* Selected effects preview */}
              {selected.length > 0 && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    {/* h2 (#355): this panel renders BEFORE the detail
                        page's sections, so its heading follows the entry's h1
                        directly. `section-header` is the same class the h2
                        section headings use, so nothing moves visually. */}
                    <h2 className="section-header" style={{ marginBottom: 0 }}>
                      With {selected.length} template{selected.length > 1 ? 's' : ''} applied
                    </h2>
                    <button
                      type="button"
                      onClick={() => setSelectedSlugs([])}
                      className="flex items-center gap-1 text-sm text-muted hover:underline"
                    >
                      <X className="w-4 h-4" /> Clear
                    </button>
                  </div>

                  {/* Modified ability scores */}
                  {abilityRows.length > 0 && (
                    <div>
                      <div className="stat-block-section">Modified Ability Scores</div>
                      <div className="stat-block-abilities">
                        {abilityRows.map(({ col, label, base, delta }) => {
                          const hasBase = base != null;
                          const newScore = hasBase ? base + delta : null;
                          return (
                            <div key={col} className="stat-block-ability">
                              <span className="stat-block-ability-name">{label}</span>
                              <span className="stat-block-ability-score">
                                {hasBase ? newScore : '—'}
                              </span>
                              <span
                                className={`stat-block-ability-mod${delta !== 0 ? ' text-dnd-purple' : ''}`}
                              >
                                {delta !== 0
                                  ? `${hasBase ? base : '?'} ${fmtDelta(delta)}`
                                  : (hasBase ? 'unchanged' : '')}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* CR + natural armor summary */}
                  <div className="flex flex-wrap gap-x-6 gap-y-1">
                    {totalCrAdj !== 0 && (
                      <div className="stat-block-row" style={{ marginBottom: 0 }}>
                        <span className="stat-block-label">Challenge Rating:</span>
                        <span className="stat-block-value">
                          {newCr != null
                            ? `${formatCR(monster.challenge_rating)} → ${formatCR(newCr)} (${fmtDelta(totalCrAdj)})`
                            : `${fmtDelta(totalCrAdj)}`}
                        </span>
                      </div>
                    )}
                    {totalNaturalArmor !== 0 && (
                      <div className="stat-block-row" style={{ marginBottom: 0 }}>
                        <span className="stat-block-label">Natural Armor:</span>
                        <span className="stat-block-value">{fmtDelta(totalNaturalArmor)}</span>
                      </div>
                    )}
                  </div>

                  {/* Per-template rules text */}
                  <div className="space-y-3">
                    {selected.map((t) => (
                      <div
                        key={t.slug}
                        className="rounded-lg p-3"
                        style={{ background: 'var(--bg-raised)', border: '1px solid var(--border)' }}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-semibold">{t.name}</span>
                          {t.source_url && (
                            <a
                              href={t.source_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs text-muted hover:underline flex-shrink-0"
                            >
                              source
                            </a>
                          )}
                        </div>
                        <div className="mt-2 space-y-1 text-sm whitespace-pre-line">
                          {t.size_type && (
                            <p><span className="stat-block-label">Size/Type:</span> {t.size_type}</p>
                          )}
                          {t.abilities && (
                            <p><span className="stat-block-label">Abilities:</span> {t.abilities}</p>
                          )}
                          {t.special_attacks && (
                            <p><span className="stat-block-label">Special Attacks:</span> {t.special_attacks}</p>
                          )}
                          {t.special_qualities && (
                            <p><span className="stat-block-label">Special Qualities:</span> {t.special_qualities}</p>
                          )}
                          {t.challenge_rating && (
                            <p><span className="stat-block-label">CR:</span> {t.challenge_rating}</p>
                          )}
                          {/* A template imported with no rules text says so (issue 1459), never an empty card. */}
                          {!(t.size_type || t.abilities || t.special_attacks || t.special_qualities || t.challenge_rating) && (
                            <p className="text-muted" data-testid="template-no-text">
                              Its rules text is not on the site yet{t.source_url ? '; the source link has it' : ''}.
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default MonsterTemplates;
